/* eslint-disable import/no-mutable-exports */
import type { DebouncedFunc } from "lodash-es";
import { debounce } from "lodash-es";
import { Notify } from "quasar";
import type { ResponseError } from "spraypaint/lib-esm/request";
import { type Router } from "vue-router";

import { getLocaleText } from "shared/boot/i18n";
import type { CreateAxiosInstanceError } from "shared/helpers/axios";
import LoadError from "shared/pages/LoadError.vue";
import type { useUserStore } from "shared/stores/user";

type ErrorHandlerError = CreateAxiosInstanceError | ResponseError;
type DebouncedErrorHandler = DebouncedFunc<
  (
    error: ErrorHandlerError
  ) => Promise<ErrorHandlerError> | ErrorHandlerError | undefined
>;

function isCreateAxiosInstanceError(
  error: CreateAxiosInstanceError | ResponseError
): error is CreateAxiosInstanceError {
  return (error as CreateAxiosInstanceError).response?.data !== undefined;
}

export function isServerError(
  error: unknown
): error is CreateAxiosInstanceError | ResponseError {
  return error !== null && typeof error === "object" && "response" in error;
}

const ONE_SECOND = 1000;

const showLoadErrorPage = (error: Error) => ({
  ...LoadError,
  error,
});

const initialErrorHandler: DebouncedErrorHandler = debounce(
  (error: ErrorHandlerError) => Promise.reject(error)
);

let unauthorizedError = initialErrorHandler;
let connectionError = initialErrorHandler;
let forbiddenError = initialErrorHandler;
let serverError = initialErrorHandler;

export default ({
  router,
  userStore,
}: {
  router: Router;
  userStore: ReturnType<typeof useUserStore>;
}) => {
  unauthorizedError = debounce(async (error: ErrorHandlerError) => {
    // Received 401, logout the user
    await userStore.logout();

    if (isCreateAxiosInstanceError(error)) {
      userStore.unauthorized(error.response!.data.message);
    }

    if (router.currentRoute.value.path !== "/sign-in") {
      // Force the browser to reload the page.
      // This is necessary as long-running browser sessions can have stale JS/CSS assets which causes
      // the app to break in a way that prevents the router from navigating to the sign-in page.
      // This, in turn, prevents the full-logout, leading to a broken state where components aren't
      // unmounted, which causes the app to break in various ways (eg, polling forever).
      const signInUrl = new URL("/sign-in", window.location.origin);

      signInUrl.searchParams.set(
        "redirect",
        router.currentRoute.value.fullPath
      );

      window.location.href = signInUrl.toString();
    }

    return error;
  }, ONE_SECOND);

  connectionError = debounce((error: ErrorHandlerError) => {
    Notify.create({
      actions: [
        {
          handler: () => router.go(0),
          icon: "sync",
          label: getLocaleText("global.retry"),
          noDismiss: true,
        },
      ],
      caption: getLocaleText("helpers.errors.connection_error_detail"),
      icon: "wifi",
      message: getLocaleText("helpers.errors.connection_error_message"),
      position: "top",
      type: "negative",
    });

    return error;
  }, ONE_SECOND);

  forbiddenError = debounce((error: ErrorHandlerError) => {
    Notify.create({
      icon: "lock",
      message: getLocaleText("helpers.errors.forbidden_message"),
      position: "top",
      type: "negative",
    });

    return error;
  }, ONE_SECOND);

  serverError = debounce((error: ErrorHandlerError) => {
    Notify.create({
      actions: [
        {
          handler: () => router.go(0),
          icon: "sync",
          label: getLocaleText("global.retry"),
          noDismiss: true,
        },
      ],
      caption: getLocaleText("helpers.errors.server_error_detail"),
      icon: "error",
      message: getLocaleText("helpers.errors.server_error_message"),
      position: "top",
      type: "negative",
    });

    return error;
  }, ONE_SECOND);
};

export {
  connectionError,
  forbiddenError,
  serverError,
  showLoadErrorPage,
  unauthorizedError,
};
